<template>
  <div class="status-card" :data-state="overallStatus">
        <div class="card-header">
          <div class="header-row">
            <p class="card-eyebrow">Service Access Status</p>
            <div class="timestamp">
              Last checked
              <span>{{ formattedLastChecked }}</span>
            </div>
          </div>
          <p class="header-description">
            The following services are critical in using the screener, to avoid inconclusive reports
          </p>
        </div>

        <div class="status-grid">
          <div
            v-for="service in serviceStatuses"
            :key="service.id"
            class="status-tile"
            :class="service.status"
          >
            <div class="status-icon" :class="service.id">
              <FontAwesomeIcon :icon="service.icon" />
            </div>
            <div class="tile-content">
              <p class="service-name">{{ service.label }}</p>
              <p class="status-label">
                {{ service.statusLabel }}
                <FontAwesomeIcon
                  v-if="service.status === 'unknown'"
                  :icon="faSpinner"
                  spin
                  class="status-spinner"
                />
              </p>
              <p class="status-description">
                {{ service.description }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="overallStatus === 'unhealthy'" class="status-alert">
          <p>
            Please review your firewall settings around the above services which are currently
            unavailable, this may also be due to services being down or internet connection issues.
          </p>
          <a :href="FIREWALL_GUIDE_URL" target="_blank" rel="noreferrer noopener">
            {{ FIREWALL_GUIDE_URL }}
          </a>
        </div>

        <div class="action-row">
          <MButton
            class="card-refresh"
            :title="isLoading ? 'Checking Services' : 'Check again'"
            variant="secondary"
            size="md"
            :icon="isLoading ? faSpinner : faRedo"
            iconPosition="left"
            :disabled="isLoading"
            @click="runChecks"
          />

          <MButton
            v-if="showContinueButton"
            class="card-continue"
            title="Continue anyway"
            variant="primary"
            size="md"
            @click="continueToApp"
          />
        </div>
      </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  faRedo,
  faMicrophone,
  faCloudBolt,
  faServer,
  faSpinner,
  faBox
} from '@fortawesome/free-solid-svg-icons'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { useServiceHealthStore, clearFirebaseFailure } from '@/stores/ServiceHealthStore'
import MButton from '@/components/MButton.vue'
import type { Ref } from 'vue'

type ServiceStatus = 'unknown' | 'healthy' | 'unhealthy'
type ServiceId = 'deepgram' | 'firebase' | 'api' | 's3'

interface ServiceDefinition {
  id: ServiceId
  label: string
  status: Ref<ServiceStatus>
  icon: IconDefinition
}

const emit = defineEmits<{
  (e: 'continue-anyway'): void
}>()

const route = useRoute()
const serviceHealthStore = useServiceHealthStore()
const FIREWALL_GUIDE_URL = _FIREWALL_GUIDE_URL_
const enforceFirewallBlock = _ENFORCE_FIREWALL_BLOCK_ === 'true'

const { deepgramStatus, firebaseStatus, apiStatus, s3Status, lastCheckedAt, hasInitialRun } =
  storeToRefs(serviceHealthStore)

const showContinueButton = computed(() => !enforceFirewallBlock && route.name !== 'status')

const continueButtonTitle = computed(() => {
  return overallStatus.value === 'healthy' ? 'Start Screener' : 'Continue anyway'
})

const isLoading = ref(false)

const services: ServiceDefinition[] = [
  { id: 'deepgram', label: 'Deepgram', status: deepgramStatus, icon: faMicrophone },
  { id: 'firebase', label: 'Firebase', status: firebaseStatus, icon: faCloudBolt },
  { id: 'api', label: 'Mable API', status: apiStatus, icon: faServer },
  { id: 's3', label: 'Screener Assets', status: s3Status, icon: faBox }
]

const statusLabels: Record<ServiceStatus, string> = {
  healthy: 'Connected',
  unhealthy: 'Needs attention',
  unknown: 'Checking...'
}

const serviceCopy: Record<
  ServiceId,
  {
    desc: string
    unhealthy: string
    helpLink?: string
  }
> = {
  deepgram: {
    desc: 'Ensures user engagement throughout screeners.',
    unhealthy: 'We could not reach Deepgram. Check your audio firewall allow list.'
  },
  firebase: {
    desc: 'Realtime progress tracking of individual screener progress and errors.',
    unhealthy: 'Realtime sync is blocked. Review the Firebase entries in the firewall guide.'
  },
  api: {
    desc: 'Where the magic happens.',
    unhealthy: 'The Mable API is blocked. The guide shows the domains to allow.'
  },
  s3: {
    desc: 'Provides access to screener assets and resources.',
    unhealthy: 'Asset storage is blocked. The guide shows the domains to allow.'
  }
}

const serviceStatuses = computed(() =>
  services.map((service) => {
    const status = service.status.value
    return {
      id: service.id,
      label: service.label,
      status,
      statusLabel: statusLabels[status],
      description:
        status === 'unhealthy' ? serviceCopy[service.id].unhealthy : serviceCopy[service.id].desc,
      icon: service.icon
    }
  })
)

const overallStatus = computed<ServiceStatus>(() => {
  if (serviceStatuses.value.some((service) => service.status === 'unhealthy')) return 'unhealthy'
  if (serviceStatuses.value.some((service) => service.status === 'unknown')) return 'unknown'
  return 'healthy'
})

const formattedLastChecked = computed(() => {
  if (isLoading.value) return 'Running now'
  if (!lastCheckedAt.value) return 'Waiting...'
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(lastCheckedAt.value))
})

const runChecks = async () => {
  if (isLoading.value) return
  isLoading.value = true
  try {
    await serviceHealthStore.runChecks()
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  if (!hasInitialRun.value) {
    void runChecks()
  }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', clearFirebaseFailure)
  }
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', clearFirebaseFailure)
  }
})

const continueToApp = () => {
  serviceHealthStore.setFailureOverride(true)
  emit('continue-anyway')
}
</script>

<style lang="scss" scoped>
$status-healthy-color: $primary;
$status-error-color: $danger;
$status-unknown-color: #c4b5fd;
$status-dark: #323444;

.action-row {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
}

.card-eyebrow {
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin: 0;
  color: rgba($status-dark, 0.6);
}

.status-card {
  width: min(720px, 100%);
  background: $white;
  border-radius: 1.75rem;
  box-shadow: 0 35px 60px rgba($black, 0.15);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  position: relative;
  border: 2px solid rgba($status-dark, 0.08);
  max-height: calc(var(--full-vh) * 0.7);
  overflow-y: auto;

  &[data-state='unhealthy'] {
    border-color: rgba($status-error-color, 0.8);
  }

  &[data-state='unknown'] {
    border-color: rgba($status-unknown-color, 0.6);
  }
}

.card-header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.card-header .header-description {
  font-size: 1rem;
  margin: 0;
  color: rgba($status-dark, 0.75);
}

.timestamp {
  font-size: 0.9rem;
  text-align: right;
  color: rgba($status-dark, 0.6);

  span {
    display: block;
    font-weight: 600;
    color: $status-dark;
  }
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 1rem;
}

.status-tile {
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  gap: 0.75rem;
  background: rgba($primary, 0.04);
  border: 1px solid rgba($primary, 0.08);
  box-shadow: inset 0 1px 0 rgba($white, 0.5);
  transition: box-shadow 0.2s ease;

  &.healthy {
    background: rgba($primary, 0.12);
    border-color: rgba($primary, 0.45);
  }

  &.unhealthy {
    background: rgba($status-error-color, 0.12);
    border-color: rgba($status-error-color, 0.6);
  }

  &.unknown {
    background: rgba($status-unknown-color, 0.15);
    border-color: rgba($status-unknown-color, 0.6);
  }
}

.status-icon {
  width: 70px;
  height: 70px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  background: rgba($primary, 0.18);
  box-shadow: inset 0 -12px 18px rgba($black, 0.08);
  color: $primary;
  flex-shrink: 0;

  :deep(svg) {
    width: 26px;
    height: 26px;
  }
}

.status-tile.unhealthy .status-icon {
  background: rgba($status-error-color, 0.18);
  color: $status-error-color;
}

.status-tile.unknown .status-icon {
  background: rgba($status-unknown-color, 0.22);
  color: $status-unknown-color;
}

.tile-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.service-name {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: $status-dark;
}

.status-label {
  margin: 0;
  font-weight: 700;
  color: $primary;
  font-size: 1.1rem;

  .status-spinner {
    margin-left: 0.5rem;
  }
}

.status-tile.unhealthy .status-label {
  color: $status-error-color;
}

.status-tile.unknown .status-label {
  color: $status-unknown-color;
}

.status-description {
  margin: 0;
  font-size: 0.9rem;
  color: rgba($status-dark, 0.7);
}

.status-tile.healthy .service-name {
  color: $primary;
}

.status-tile.healthy .status-description {
  color: rgba($primary, 0.85);
}

.status-alert {
  width: 100%;
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid rgba($status-error-color, 0.2);
  background: rgba($status-error-color, 0.05);
  color: rgba($status-error-color, 0.8);
  font-weight: 600;
  font-size: 0.95rem;

  a {
    color: inherit;
    text-decoration: underline;
    font-weight: 700;
    display: inline-block;
    margin-top: 0.25rem;
  }
}

.card-refresh {
  align-self: flex-start;
  box-shadow: 0 10px 30px rgba($primary, 0.3);
}

.card-continue {
  align-self: flex-start;
  margin-top: 0.5rem;
}

@media screen and (max-width: 768px) {
  .status-card {
    width: 100%;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .timestamp {
    text-align: left;
  }
}
</style>
